import React, { useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';

/* ────────────────────────────────────────────────────────────
   Bitcoin Holder Segmentation — two-axis prototype
   Axis 1: Ideology (Saylor's four): Maximalist / Capitalist /
           Technologist / Fundamentalist
   Axis 2: Conviction–Horizon: Holder → Allocator → Trader → Degen
   Part C: optional flavor tags per ideology.
   Plus sincerity flags for contradictory combinations.
──────────────────────────────────────────────────────────── */

const ORANGE = '#F7931A';
const BLACK = '#0b0b0d';
const DARK = '#141417';
const CARD = '#1b1b1f';

/* ── Axis 1: Ideologies ── */
type IdeologyId = 'maximalist' | 'capitalist' | 'technologist' | 'fundamentalist';

interface Ideology {
  id: IdeologyId;
  name: string;
  emoji: string;
  tagline: string;
  description: string;
}

const IDEOLOGIES: Ideology[] = [
  {
    id: 'maximalist', name: 'Maximalist', emoji: '⚡',
    tagline: 'There is no second best.',
    description: 'Bitcoin is the one true sound money — everything else is noise. Conviction isn\'t a position for you, it\'s an identity. You\'ve said "have fun staying poor" and meant it as a blessing.'
  },
  {
    id: 'capitalist', name: 'Capitalist', emoji: '🏦',
    tagline: 'Digital capital for global finance.',
    description: 'You see Bitcoin the way the economy once absorbed electricity or the internet: a new capital asset to be woven into treasuries, funds, credit and capital markets. Saylor is your faction\'s principal — and you know it.'
  },
  {
    id: 'technologist', name: 'Technologist', emoji: '🛠️',
    tagline: 'Don\'t trust, verify — then improve.',
    description: 'Bitcoin is a protocol to be engineered: scalability, privacy, functionality, security — balanced against the risk that too much change destabilizes the system. You read release notes for pleasure.'
  },
  {
    id: 'fundamentalist', name: 'Fundamentalist', emoji: '🗽',
    tagline: 'Guardian of first principles.',
    description: 'Sovereignty, decentralization, the culture of verification, distrust of centralized power. Custodians and ETFs betray the reason Bitcoin exists. Your keys, your coins, your rules.'
  },
];

/* ── Axis 2: Conviction bands ── */
type BandId = 'holder' | 'allocator' | 'trader' | 'degen';

interface Band {
  id: BandId;
  name: string;
  emoji: string;
  min: number;
  max: number;
  blurb: string;
}

const BANDS: Band[] = [
  { id: 'holder', name: 'Conviction Holder', emoji: '💎', min: 14, max: 18, blurb: 'Anchored to a long thesis. Price-insensitive. Exit is not part of the plan.' },
  { id: 'allocator', name: 'Strategic Allocator', emoji: '⚖️', min: 9, max: 13, blurb: 'Sized and rebalanced to a thesis. Thinks in risk-adjusted terms.' },
  { id: 'trader', name: 'Active Trader', emoji: '📈', min: 4, max: 8, blurb: 'Works the market on a short horizon. Targets and stops.' },
  { id: 'degen', name: 'Speculator / Degen', emoji: '🎰', min: 0, max: 3, blurb: 'Thesis-free, in it for the move. The reflexive, marginal money that actually moves price.' },
];

/* ── Part A: Ideology (8 items, letter key from the doc) ── */
const PART_A_KEY: Record<number, Record<string, IdeologyId>> = {
  1: { a: 'maximalist', b: 'capitalist', c: 'technologist', d: 'fundamentalist' },
  2: { a: 'fundamentalist', b: 'technologist', c: 'maximalist', d: 'capitalist' },
  3: { a: 'capitalist', b: 'maximalist', c: 'fundamentalist', d: 'technologist' },
  4: { a: 'technologist', b: 'fundamentalist', c: 'capitalist', d: 'maximalist' },
  5: { a: 'fundamentalist', b: 'maximalist', c: 'technologist', d: 'capitalist' },
  6: { a: 'capitalist', b: 'technologist', c: 'fundamentalist', d: 'maximalist' },
  7: { a: 'technologist', b: 'capitalist', c: 'maximalist', d: 'fundamentalist' },
  8: { a: 'capitalist', b: 'technologist', c: 'fundamentalist', d: 'maximalist' },
};

const PART_A: { q: string; answers: string[] }[] = [
  {
    q: 'The point of Bitcoin is…',
    answers: [
      'that it\'s the best money ever created — everything else is noise.',
      'that it plugs into balance sheets, funds, and capital markets.',
      'the protocol and the software we can keep improving.',
      'that no government or company can control it.',
    ],
  },
  {
    q: 'Bitcoin succeeds when…',
    answers: [
      'more people hold their own keys and run their own nodes.',
      'the technology becomes more robust and capable.',
      'it becomes the world\'s monetary standard.',
      'institutions and corporations adopt it at scale.',
    ],
  },
  {
    q: 'The honest take on the rest of the market:',
    answers: [
      'Treating Bitcoin as a core asset class in a real strategy makes sense.',
      'Altcoins are a distraction; Bitcoin-only conviction is correct.',
      'Custodians and ETFs betray the reason Bitcoin exists.',
      'The interesting frontier is what we can build on and around it.',
    ],
  },
  {
    q: 'Bitcoin drops 40% this week. My first thought:',
    answers: [
      'I care more about what\'s shipping than the chart.',
      'I still hold my own keys — nothing important changed.',
      'Time to think about rebalancing or buying the dip.',
      'My conviction doesn\'t move.',
    ],
  },
  {
    q: 'The most valuable thing I can do for Bitcoin:',
    answers: [
      'Self-custody and verify everything myself.',
      'Hold, and never sell.',
      'Build or support the technology.',
      'Allocate capital to it intelligently.',
    ],
  },
  {
    q: 'Bitcoin essentially is:',
    answers: [
      'Digital capital — the next thing global finance integrates.',
      'A protocol that\'s still being engineered.',
      'A tool for individual sovereignty against the state.',
      'A belief system as much as an asset.',
    ],
  },
  {
    q: 'Who resonates with me most:',
    answers: [
      'The core developers and protocol designers.',
      'The treasury and capital-markets strategists.',
      'The "never sell" conviction crowd.',
      'The cypherpunks and self-custody hardliners.',
    ],
  },
  {
    q: 'The greatest threat to Bitcoin:',
    answers: [
      'Bad regulation and missing institutional rails.',
      'Technical stagnation, or unsafe changes to the protocol.',
      'Creeping centralization and surveillance.',
      'People losing faith and selling.',
    ],
  },
];

/* ── Part B: Conviction (6 items, letter key → points H=3, A=2, T=1, S=0) ── */
const PART_B_KEY: Record<number, Record<string, number>> = {
  1: { a: 2, b: 0, c: 3, d: 1 },
  2: { a: 1, b: 3, c: 0, d: 2 },
  3: { a: 0, b: 2, c: 3, d: 1 },
  4: { a: 0, b: 3, c: 2, d: 1 },
  5: { a: 1, b: 2, c: 0, d: 3 },
  6: { a: 3, b: 0, c: 1, d: 2 },
};

const PART_B: { q: string; answers: string[] }[] = [
  {
    q: 'My typical holding plan:',
    answers: [
      'Years — sized to a long-term thesis.',
      'However long it takes to catch the next move.',
      'Indefinite — I don\'t intend to sell.',
      'Days to weeks — I move in and out.',
    ],
  },
  {
    q: 'If Bitcoin fell 50% tomorrow:',
    answers: [
      'I\'d already have had a plan or a stop.',
      'Irrelevant — I\'m not selling.',
      'I\'d be hunting the bounce to trade.',
      'I\'d look to add at a better basis.',
    ],
  },
  {
    q: 'What gets me to exit a position:',
    answers: [
      'A faster opportunity somewhere else.',
      'A genuine change in the long-term thesis.',
      'Nothing — exiting isn\'t part of the plan.',
      'A price target or a stop being hit.',
    ],
  },
  {
    q: 'Honestly, the main reason I hold:',
    answers: [
      'The chance to make a lot of money, fast.',
      'I believe in what Bitcoin fundamentally is.',
      'It earns its place in my portfolio.',
      'It\'s a liquid, volatile market I can work.',
    ],
  },
  {
    q: 'How often I look at the price:',
    answers: [
      'Daily, often intraday.',
      'Periodically, mostly to rebalance.',
      'Constantly — I don\'t want to miss a move.',
      'Rarely — it doesn\'t change what I do.',
    ],
  },
  {
    q: '"Number go up" is:',
    answers: [
      'Confirmation of a thesis I already held.',
      'The entire point.',
      'The game — played with discipline.',
      'One input into how the position is performing.',
    ],
  },
];

/* ── Part C: flavor per primary ideology ── */
const PART_C: Record<IdeologyId, { q: string; answers: { text: string; tag: string }[] }> = {
  technologist: {
    q: 'The one frontier I\'d pick:',
    answers: [
      { text: 'Base-layer consensus, security, and stability.', tag: 'Core Engineer' },
      { text: 'Lightning, payments, and throughput.', tag: 'Scaling Builder' },
      { text: 'Privacy, fungibility, and coin-mixing.', tag: 'Privacy Engineer' },
    ],
  },
  fundamentalist: {
    q: '"First principles" means, most:',
    answers: [
      { text: 'Self-custody and verifying everything myself.', tag: 'Sovereign' },
      { text: 'Separating money from the state.', tag: 'Libertarian' },
      { text: 'Financial privacy as a fundamental right.', tag: 'Cypherpunk' },
    ],
  },
  maximalist: {
    q: 'My conviction mostly shows up as:',
    answers: [
      { text: 'Understanding the monetary theory deeply.', tag: 'Sound-Money Ideologue' },
      { text: 'Bringing other people in.', tag: 'Orange-Pill Evangelist' },
    ],
  },
  capitalist: {
    q: 'Tick if true:',
    answers: [
      { text: 'I\'m specifically drawn to Bitcoin as collateral: credit, derivatives, convertibles, treasury structures.', tag: 'Financial Engineer' },
      { text: 'Not really — I just allocate.', tag: 'Strategic Allocator' },
    ],
  },
};

/* ── 4×4 cell flavor text ── */
const CELL_NOTES: Record<string, string> = {
  'maximalist|holder': 'True believer — the core archetype. Time horizon measured in halvings.',
  'maximalist|allocator': 'Conviction-led but portfolio-aware. Rare breed.',
  'maximalist|trader': 'Tension: trades a coin they vow never to sell. Interesting.',
  'maximalist|degen': 'Contradiction detected — identity cosplay or careless clicking?',
  'capitalist|holder': 'Long-horizon treasury holder — Strategy-style.',
  'capitalist|allocator': 'The institutional core. Most coherent cell on the map.',
  'capitalist|trader': 'Active-markets participant. Coherent and unbothered.',
  'capitalist|degen': 'Yield / leverage chaser. At least the paperwork is clean.',
  'technologist|holder': 'Builder who also believes long-term.',
  'technologist|allocator': 'Builder with a measured stack.',
  'technologist|trader': 'Uncommon — builders rarely day-trade.',
  'technologist|degen': 'Rare. Possibly just here for the hackathons.',
  'fundamentalist|holder': 'Self-custody sovereign — the core archetype. Steel plates and no exit plan.',
  'fundamentalist|allocator': 'Principled holder with structure.',
  'fundamentalist|trader': 'Tension with the first-principles ethos.',
  'fundamentalist|degen': 'Contradiction detected — flag as insincere.',
};

const FLAG_CELLS = new Set(['maximalist|degen', 'fundamentalist|degen']);

/* ── Styling ── */

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(247,147,26,0.4); }
  50% { box-shadow: 0 0 0 14px rgba(247,147,26,0); }
`;

const Page = styled.div`
  min-height: 100vh;
  background: radial-gradient(ellipse at top, #1a1205 0%, ${BLACK} 55%);
  color: #eee;
  font-family: -apple-system, 'Segoe UI', Roboto, sans-serif;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 16px 80px;
`;

const Logo = styled.div`
  font-weight: 900; font-size: 15px; letter-spacing: 2px;
  color: ${ORANGE}; text-transform: uppercase; margin-bottom: 6px;
`;

const H1 = styled.h1`
  font-size: clamp(30px, 6vw, 52px); font-weight: 900; text-align: center;
  line-height: 1.05; margin: 12px 0 8px;
  span { color: ${ORANGE}; }
`;

const Sub = styled.p`
  color: #999; text-align: center; max-width: 560px;
  font-size: 16px; line-height: 1.5; margin: 0 0 32px;
`;

const BigButton = styled.button`
  background: ${ORANGE}; color: #000; border: none; font-weight: 800;
  font-size: 17px; padding: 16px 40px; border-radius: 999px; cursor: pointer;
  animation: ${pulse} 2.2s infinite; transition: transform 0.15s;
  &:hover { transform: scale(1.05); }
`;

const GhostButton = styled.button`
  background: transparent; color: #aaa; border: 1px solid #333; font-weight: 600;
  font-size: 14px; padding: 12px 26px; border-radius: 999px; cursor: pointer; margin-top: 14px;
  &:hover { border-color: ${ORANGE}; color: ${ORANGE}; }
`;

const QuizCard = styled.div`
  background: ${CARD}; border: 1px solid #2a2a2e; border-radius: 18px;
  padding: 28px; width: 100%; max-width: 620px;
`;

const PartLabel = styled.div`
  color: #888; font-size: 11px; letter-spacing: 2px; text-transform: uppercase;
  margin-bottom: 6px; font-weight: 700;
  span { color: ${ORANGE}; }
`;

const Progress = styled.div`
  height: 6px; background: #26262b; border-radius: 3px; overflow: hidden; margin-bottom: 22px;
  div { height: 100%; background: ${ORANGE}; transition: width 0.3s; }
`;

const QNum = styled.div`
  color: ${ORANGE}; font-weight: 700; font-size: 12px; letter-spacing: 1.5px;
  text-transform: uppercase; margin-bottom: 8px;
`;

const QText = styled.h2`
  font-size: 21px; font-weight: 800; margin: 0 0 20px; color: #fff; line-height: 1.3;
`;

const Answer = styled.button`
  display: block; width: 100%; text-align: left; background: ${DARK};
  border: 1px solid #2e2e33; color: #ddd; padding: 14px 16px; border-radius: 12px;
  font-size: 15px; margin-bottom: 10px; cursor: pointer; transition: all 0.15s; line-height: 1.4;
  &:hover { border-color: ${ORANGE}; background: #221a10; transform: translateX(4px); }
`;

const ResultCard = styled.div`
  width: 100%; max-width: 560px;
  background: linear-gradient(160deg, #1c1305 0%, ${CARD} 60%);
  border: 1px solid ${ORANGE}44; border-radius: 24px; padding: 36px 32px;
  text-align: center; position: relative; overflow: hidden;
`;

const Watermark = styled.div`
  position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; pointer-events: none;
  span { font-size: 52px; font-weight: 900; color: rgba(247,147,26,0.07); transform: rotate(-18deg); letter-spacing: 6px; white-space: nowrap; text-transform: uppercase; }
`;

const BadgeCircle = styled.div`
  width: 110px; height: 110px; margin: 0 auto 16px; border-radius: 50%;
  background: radial-gradient(circle at 35% 30%, #ffb84d, ${ORANGE} 60%, #8a4d00);
  display: flex; align-items: center; justify-content: center; font-size: 52px;
  box-shadow: 0 0 40px rgba(247,147,26,0.45); border: 3px solid #ffcf8f;
`;

const ArchName = styled.h2`
  font-size: 28px; font-weight: 900; color: ${ORANGE}; margin: 0 0 4px; line-height: 1.2;
`;

const Tagline = styled.div`
  font-style: italic; color: #ccc; margin-bottom: 18px; font-size: 16px;
`;

const Desc = styled.p`
  color: #bbb; font-size: 15px; line-height: 1.6; margin: 0 0 18px;
`;

const FlagBox = styled.div`
  background: #2a1205; border: 1px solid #f4433655; color: #ff8a80;
  border-radius: 10px; padding: 10px 14px; font-size: 13px; margin-bottom: 16px;
`;

const ScoreRow = styled.div`
  display: flex; align-items: center; gap: 10px; margin-bottom: 8px; text-align: left;
  .label { width: 150px; font-size: 13px; color: #ccc; font-weight: 600; }
  .bar { flex: 1; height: 10px; background: #26262b; border-radius: 5px; overflow: hidden;
    div { height: 100%; background: ${ORANGE}; border-radius: 5px; } }
  .pct { width: 40px; font-size: 12px; color: #888; text-align: right; }
`;

const ConvictionMeter = styled.div`
  margin: 18px auto 6px; max-width: 420px;
  .track { position: relative; height: 12px; border-radius: 6px;
    background: linear-gradient(90deg, #f44336 0%, #ff9800 35%, #ffc107 65%, ${ORANGE} 100%); }
  .marker { position: absolute; top: -5px; width: 4px; height: 22px; background: #fff; border-radius: 2px; transform: translateX(-2px); box-shadow: 0 0 8px #fff; }
  .labels { display: flex; justify-content: space-between; font-size: 10px; color: #888; margin-top: 6px; }
`;

const HandleInput = styled.input`
  background: ${DARK}; border: 1px solid #333; border-radius: 10px; color: #fff;
  padding: 12px 16px; font-size: 15px; width: 260px; text-align: center;
  margin: 16px 0 4px; outline: none;
  &:focus { border-color: ${ORANGE}; }
`;

const ShareRow = styled.div`
  display: flex; gap: 10px; justify-content: center; flex-wrap: wrap; margin-top: 18px;
`;

const ShareBtn = styled.a`
  background: #000; border: 1px solid #333; color: #fff; padding: 10px 20px;
  border-radius: 999px; font-size: 14px; font-weight: 700; text-decoration: none; cursor: pointer;
  &:hover { border-color: ${ORANGE}; color: ${ORANGE}; }
`;

const PaidHint = styled.div`
  margin-top: 22px; padding: 14px 18px; border: 1px dashed #444;
  border-radius: 12px; font-size: 13px; color: #999; max-width: 560px;
  b { color: ${ORANGE}; }
`;

const ArchGrid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 12px; max-width: 760px; width: 100%; margin-top: 44px;
`;

const ArchTile = styled.div`
  background: ${CARD}; border: 1px solid #26262b; border-radius: 14px;
  padding: 16px 12px; text-align: center; transition: all 0.15s; cursor: default;
  &:hover { border-color: ${ORANGE}66; transform: translateY(-3px); }
  .e { font-size: 32px; margin-bottom: 6px; }
  .n { font-weight: 800; font-size: 13px; color: #fff; }
  .t { font-size: 11px; color: #888; margin-top: 4px; font-style: italic; }
`;

const BandRow = styled.div`
  display: flex; gap: 8px; justify-content: center; flex-wrap: wrap; margin-top: 20px; max-width: 720px;
  span { background: ${DARK}; border: 1px solid #2a2a2e; border-radius: 999px; padding: 8px 14px; font-size: 12px; color: #aaa; }
`;

/* ── Component ── */

type Stage = 'landing' | 'partA' | 'partB' | 'partC' | 'result';

const PersonaTest: React.FC = () => {
  const [stage, setStage] = useState<Stage>('landing');
  const [qIndex, setQIndex] = useState(0);
  const [answersA, setAnswersA] = useState<string[]>([]);
  const [answersB, setAnswersB] = useState<string[]>([]);
  const [flavorTag, setFlavorTag] = useState<string | null>(null);
  const [handle, setHandle] = useState('');

  const ideologyScores = useMemo(() => {
    const tally: Record<IdeologyId, number> = { maximalist: 0, capitalist: 0, technologist: 0, fundamentalist: 0 };
    answersA.forEach((letter, i) => {
      const ideology = PART_A_KEY[i + 1]?.[letter];
      if (ideology) tally[ideology]++;
    });
    return tally;
  }, [answersA]);

  const ideologyRanking = useMemo(() => {
    return IDEOLOGIES
      .map(a => ({ arch: a, score: ideologyScores[a.id] }))
      .sort((a, b) => b.score - a.score);
  }, [ideologyScores]);

  const convictionScore = useMemo(() => {
    return answersB.reduce((sum, letter, i) => sum + (PART_B_KEY[i + 1]?.[letter] ?? 0), 0);
  }, [answersB]);

  const band = useMemo(() => {
    return BANDS.find(b => convictionScore >= b.min && convictionScore <= b.max) || BANDS[3];
  }, [convictionScore]);

  const primary = ideologyRanking[0]?.arch;
  const secondary = ideologyRanking[1];
  const hasSecondary = secondary && secondary.score > 0 && (ideologyRanking[0].score - secondary.score) <= 2;

  const cellNote = primary ? CELL_NOTES[`${primary.id}|${band.id}`] : null;
  const flagged = primary ? FLAG_CELLS.has(`${primary.id}|${band.id}`) : false;

  const totalQuestions = PART_A.length + PART_B.length + 1;
  const answeredCount = answersA.length + answersB.length + (flavorTag !== null ? 1 : 0);

  const answerA = (idx: number) => {
    const letter = 'abcd'[idx];
    const next = [...answersA, letter];
    setAnswersA(next);
    if (next.length < PART_A.length) {
      setQIndex(next.length);
    } else {
      setStage('partB');
      setQIndex(0);
    }
  };

  const answerB = (idx: number) => {
    const letter = 'abcd'[idx];
    const next = [...answersB, letter];
    setAnswersB(next);
    if (next.length < PART_B.length) {
      setQIndex(next.length);
    } else {
      setStage('partC');
      setQIndex(0);
    }
  };

  const answerC = (tag: string) => {
    setFlavorTag(tag);
    setStage('result');
  };

  const restart = () => {
    setStage('landing');
    setQIndex(0);
    setAnswersA([]);
    setAnswersB([]);
    setFlavorTag(null);
    setHandle('');
  };

  const cleanHandle = handle.replace(/^@/, '').trim();
  const resultTitle = primary
    ? `${primary.name} · ${band.name}${flavorTag && flavorTag !== 'Strategic Allocator' ? ` · ${flavorTag}` : ''}`
    : '';
  const shareText = primary
    ? `My Bitcoin holder segment: ${resultTitle} ${primary.emoji}${band.emoji} — what's yours?`
    : '';
  const shareUrl = 'https://proofofprice.com/persona';
  const tweetIntent = `https://x.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;

  return (
    <Page>
      <Logo>₿ ProofOfPrice</Logo>

      {stage === 'landing' && (
        <>
          <H1>What kind of <span>Bitcoin holder</span> are you?</H1>
          <Sub>
            A two-axis segmentation built on Saylor's four Bitcoin ideologies,
            crossed with a conviction–horizon spectrum. 15 questions.
            Unlabeled answers. Honest results.
          </Sub>
          <BigButton onClick={() => setStage('partA')}>Start the Test →</BigButton>
          <GhostButton onClick={() => document.getElementById('arch-list')?.scrollIntoView({ behavior: 'smooth' })}>
            Meet the four ideologies
          </GhostButton>

          <BandRow>
            <span>💎 Conviction Holder</span>
            <span>⚖️ Strategic Allocator</span>
            <span>📈 Active Trader</span>
            <span>🎰 Speculator / Degen</span>
          </BandRow>

          <ArchGrid id="arch-list">
            {IDEOLOGIES.map(a => (
              <ArchTile key={a.id}>
                <div className="e">{a.emoji}</div>
                <div className="n">{a.name}</div>
                <div className="t">{a.tagline}</div>
              </ArchTile>
            ))}
          </ArchGrid>
        </>
      )}

      {stage === 'partA' && (
        <QuizCard>
          <PartLabel>Part <span>A</span> · Why are you here</PartLabel>
          <Progress><div style={{ width: `${(answeredCount / totalQuestions) * 100}%` }} /></Progress>
          <QNum>Question {qIndex + 1} / {PART_A.length}</QNum>
          <QText>{PART_A[qIndex].q}</QText>
          {PART_A[qIndex].answers.map((text, i) => (
            <Answer key={i} onClick={() => answerA(i)}>{text}</Answer>
          ))}
        </QuizCard>
      )}

      {stage === 'partB' && (
        <QuizCard>
          <PartLabel>Part <span>B</span> · How committed are you</PartLabel>
          <Progress><div style={{ width: `${(answeredCount / totalQuestions) * 100}%` }} /></Progress>
          <QNum>Question {qIndex + 1} / {PART_B.length}</QNum>
          <QText>{PART_B[qIndex].q}</QText>
          {PART_B[qIndex].answers.map((text, i) => (
            <Answer key={i} onClick={() => answerB(i)}>{text}</Answer>
          ))}
        </QuizCard>
      )}

      {stage === 'partC' && primary && (
        <QuizCard>
          <PartLabel>Part <span>C</span> · Optional flavor — {primary.name} edition</PartLabel>
          <Progress><div style={{ width: `${(answeredCount / totalQuestions) * 100}%` }} /></Progress>
          <QNum>One last thing</QNum>
          <QText>{PART_C[primary.id].q}</QText>
          {PART_C[primary.id].answers.map((a, i) => (
            <Answer key={i} onClick={() => answerC(a.tag)}>{a.text}</Answer>
          ))}
          <GhostButton onClick={() => answerC('')}>Skip</GhostButton>
        </QuizCard>
      )}

      {stage === 'result' && primary && (
        <>
          <H1 style={{ fontSize: 'clamp(24px, 4vw, 36px)' }}>Your segment:</H1>
          <ResultCard>
            <Watermark><span>proofofprice.com</span></Watermark>
            <BadgeCircle>{primary.emoji}</BadgeCircle>
            <ArchName>{primary.name} · {band.name}</ArchName>
            <Tagline>"{primary.tagline}"</Tagline>

            {flavorTag && flavorTag !== 'Strategic Allocator' && flavorTag !== '' && (
              <div style={{ fontSize: '13px', color: ORANGE, marginBottom: '12px', fontWeight: 700 }}>
                Flavor: {flavorTag}
              </div>
            )}
            {hasSecondary && (
              <div style={{ fontSize: '13px', color: '#999', marginBottom: '12px' }}>
                Secondary leaning: {secondary.arch.emoji} {secondary.arch.name}
              </div>
            )}

            {flagged && (
              <FlagBox>
                ⚠️ Consistency flag: {primary.name} × {band.name} is an internally
                contradictory combination. Answering aspirationally? We won't tell.
              </FlagBox>
            )}

            {cellNote && <Desc>{cellNote}</Desc>}
            <Desc>{primary.description}</Desc>

            <div style={{ margin: '0 auto 6px', maxWidth: '440px' }}>
              {ideologyRanking.map(({ arch, score }) => (
                <ScoreRow key={arch.id}>
                  <div className="label">{arch.emoji} {arch.name}</div>
                  <div className="bar"><div style={{ width: `${Math.round((score / PART_A.length) * 100)}%` }} /></div>
                  <div className="pct">{score}/{PART_A.length}</div>
                </ScoreRow>
              ))}
            </div>

            <ConvictionMeter>
              <div className="track">
                <div className="marker" style={{ left: `${(convictionScore / 18) * 100}%` }} />
              </div>
              <div className="labels">
                <span>🎰 Degen (0)</span>
                <span>📈 Trader</span>
                <span>⚖️ Allocator</span>
                <span>💎 Holder (18)</span>
              </div>
              <div style={{ textAlign: 'center', fontSize: '13px', color: '#ccc', marginTop: '8px' }}>
                Conviction score: <b style={{ color: ORANGE }}>{convictionScore}/18</b> — {band.emoji} {band.name}
              </div>
            </ConvictionMeter>

            <HandleInput
              placeholder="@your_x_handle (optional)"
              value={handle}
              onChange={e => setHandle(e.target.value)}
            />
            {cleanHandle && (
              <div style={{ fontSize: '13px', color: '#888' }}>
                Your share card will read: <b style={{ color: ORANGE }}>@{cleanHandle}</b> — certified {primary.name} · {band.name}
              </div>
            )}
          </ResultCard>

          <ShareRow>
            <ShareBtn href={tweetIntent} target="_blank" rel="noreferrer">𝕏 Share result</ShareBtn>
            <ShareBtn as="button" onClick={restart}>↻ Retake</ShareBtn>
          </ShareRow>

          <PaidHint>
            🚧 <b>Prototype:</b> the final version generates a personalized share graphic
            (badge + your handle) for <b>500 sats</b>, and a full segment report with an AI
            analysis of your last 1,000 posts for ideology–behavior congruency — <b>2,000 sats</b>.
            Paid via Lightning.
          </PaidHint>
        </>
      )}
    </Page>
  );
};

export default PersonaTest;
